import { Box, Button } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { quickReactionsAtom } from '../../store/emoji';
import { EMOJI_FONT } from '../../lib/emoji';
import { useT } from '../../i18n';

// The quick reaction row (M69, D264): your six most recent reactions (the
// D21 defaults until you have some), 32 px emoji on 44 px tall targets, and
// ＋ for any other emoji. Used by the phone's actions sheet and the desktop
// message menu. Seven equal columns with no gaps, each up to 48 px wide, so
// the row never scrolls sideways: 44 px wide from a 360 px phone up, about
// 42 px on a 320 px one.
const cellProps = {
  variant: 'ghost',
  colorScheme: 'gray',
  w: '100%',
  maxW: '48px',
  minW: 0,
  h: '44px',
  p: 0,
  justifySelf: 'center',
  borderRadius: 'full',
} as const;

export function QuickReactions({
  onReact,
  onMore,
}: {
  onReact: (emoji: string) => void;
  onMore: () => void;
}) {
  const t = useT();
  const emojis = useAtomValue(quickReactionsAtom);
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(7, minmax(0, 1fr))"
      pb={2}
      role="group"
      aria-label={t('chat.actions.react')}
      data-testid="quick-reactions"
    >
      {emojis.map((emoji) => (
        <Button
          key={emoji}
          {...cellProps}
          aria-label={t('chat.actions.reactWith', { emoji })}
          onClick={() => onReact(emoji)}
        >
          <Box
            as="span"
            fontSize="32px"
            lineHeight="1"
            fontFamily={EMOJI_FONT}
            aria-hidden="true"
          >
            {emoji}
          </Box>
        </Button>
      ))}
      <Button
        {...cellProps}
        bg="surface.subtle"
        color="ink"
        aria-label={t('chat.emoji.more')}
        title={t('chat.emoji.more')}
        data-testid="more-reactions"
        onClick={onMore}
      >
        <Plus size={22} aria-hidden="true" />
      </Button>
    </Box>
  );
}
